<script>
    import ProviderObject from '@/components/page-main/ProviderObject.vue';
    import { getClassificationProgress } from '@/api/providers.js';
    import { getDisputesSummary } from '@/api/file.js';
    import { filterConflicts, filterProviders, filterReady, sortProviders } from '@/scripts/providerSearch.mjs';

    // Remembered per browser (869fch4ym).
    const PREFS_KEY = 'main-provider-filters';
    function loadPrefs() {
        try { return JSON.parse(localStorage.getItem(PREFS_KEY)) ?? {}; } catch { return {}; }
    }


    export default {
        components: {
            ProviderObject: ProviderObject,
        },
        data() {
            return {
                // { [providerID]: { total, classified } }, empty until loaded and
                // empty forever if the request failed. A provider missing from it
                // simply shows no progress badge.
                classification: {},
                // Client-side filter over the store, no request. Switching mode
                // keeps the text; its meaning changes.
                query: '',
                mode: 'provider',
                onlyReady: loadPrefs().onlyReady === true,
                sort: ['az', 'za', 'recent'].includes(loadPrefs().sort) ? loadPrefs().sort : 'az',
                // Open GUP disputes (869fheb1h). Not remembered: a page that opens
                // already hiding providers is a surprise. summary null = failed.
                conflicts: 'all',
                summary: null,
                summaryTicket: 0,
            }
        },
        async created() {
            this.classification = await getClassificationProgress();
        },
        watch: {
            // Fresh counts on every switch: disputes are resolved on another page.
            // Cleared first, so a list from the last visit never stands in while
            // loading; only the latest request may write.
            async conflicts(v) {
                this.summary = null;
                if (v === 'all') return;
                const ticket = ++this.summaryTicket;
                const fresh = await getDisputesSummary();
                if (ticket === this.summaryTicket) this.summary = fresh;
            },
            onlyReady() { this.savePrefs(); },
            sort() { this.savePrefs(); },
        },
        methods: {
            savePrefs() {
                try {
                    localStorage.setItem(PREFS_KEY, JSON.stringify({ onlyReady: this.onlyReady, sort: this.sort }));
                } catch { /* private mode */ }
            },
        },
        computed: {
            providerAndGames() { return this.$store.getters.PROVIDERS_AND_GAMES },
            filteredProviders() {
                if (this.summaryFailed) return [];
                const found = filterProviders(this.providerAndGames, this.query, this.mode);
                const ready = this.onlyReady ? filterReady(found) : found;
                return sortProviders(filterConflicts(ready, this.summary, this.conflicts), this.sort);
            },
            isFiltering() { return this.query.trim() !== '' || this.onlyReady || this.conflicts !== 'all' },
            // Until the summary answers, or when it failed, show nothing rather
            // than a list that looks conflict-free.
            summaryFailed() { return this.conflicts !== 'all' && this.summary == null },
            // Unfiltered, the header keeps the store's own counts (they come from
            // separate requests and may legitimately differ from the tree).
            // Filtered, it counts what is on screen.
            providerCount() {
                return this.isFiltering
                    ? this.filteredProviders.length
                    : this.$store.getters.PROVIDER_COUNT;
            },
            gameCount() {
                return this.isFiltering
                    ? this.filteredProviders.reduce((n, p) => n + (p.game_name ?? []).length, 0)
                    : this.$store.getters.GAME_COUNT;
            },
            emptyText() {
                if (this.summaryFailed) return 'Loading conflicts… If this stays, reload the page.';
                if (this.query.trim()) return `Nothing matches "${this.query.trim()}".`;
                return 'Nothing matches the filters.';
            },
            placeholder() {
                return this.mode === 'game' ? 'Find game in all providers…' : 'Find provider…';
            },
        }
    }
</script>


<template>
    <div class="right_header">
        <label>Provider Count: {{ providerCount }}</label>
        <label class="ml_5">| Game Count: {{ gameCount }}</label>
        <div class="provider_search">
            <input
                class="provider_search_input"
                type="text"
                :placeholder="placeholder"
                aria-label="Search providers or games"
                v-model="query"
            />
            <button
                v-if="query"
                type="button"
                class="provider_search_clear"
                aria-label="Clear search"
                @click="query = ''"
            >×</button>
            <button
                type="button"
                class="provider_search_mode"
                :class="{ active: mode === 'provider' }"
                :aria-pressed="mode === 'provider'"
                @click="mode = 'provider'"
            >provider</button>
            <button
                type="button"
                class="provider_search_mode"
                :class="{ active: mode === 'game' }"
                :aria-pressed="mode === 'game'"
                @click="mode = 'game'"
            >game</button>
        </div>
    </div>

    <div class="provider_filters">
        <span class="provider_filters_label">Filters</span>
        <label class="provider_filters_item">
            <input type="checkbox" v-model="onlyReady">
            Only ready to release
        </label>
        <label class="provider_filters_item" title="Open disputes from GUP uploads">
            Conflicts
            <select v-model="conflicts">
                <option value="all">All</option>
                <option value="providers-clean">Providers without conflicts</option>
                <option value="games-conflict">Games with conflicts</option>
                <option value="games-clean">Games without conflicts</option>
            </select>
        </label>
        <label class="provider_filters_item">
            Sort
            <select v-model="sort">
                <option value="az">A → Z</option>
                <option value="za">Z → A</option>
                <option value="recent">Recently updated</option>
            </select>
        </label>
    </div>

    <ProviderObject
        v-for = "providerValue in filteredProviders" :key="providerValue['id']"
        :provider = "providerValue"
        :classification = "classification[providerValue['id']] || null"
    />
    <p
        v-if="isFiltering && filteredProviders.length === 0"
        class="provider_search_empty"
    >{{ emptyText }}</p>
</template>


<style scoped>
    .provider_filters {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 20px;
        margin: 0 0 10px;
        padding: 8px 12px;
        border: 1px solid var(--header-separator);
        border-radius: 5px;
        font-size: 14px;
    }
    .provider_filters_label {
        font-size: 11px;
        font-weight: bold;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        opacity: 0.7;
    }
    .provider_filters_item {
        display: flex;
        align-items: center;
        gap: 6px;
        cursor: pointer;
    }
    .provider_filters_item select {
        padding: 3px 6px;
        border: 1px solid var(--header-separator);
        border-radius: 4px;
        background-color: var(--background);
        color: var(--header-text);
        font: inherit;
    }
    .provider_search {
        margin-left: auto;
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 6px;
    }
    .provider_search_input {
        width: 260px;
        padding: 3px 8px;
        border: 1px solid var(--header-separator);
        border-radius: 4px;
        background-color: var(--background);
        color: var(--header-text);
        font-size: 14px;
    }
    .provider_search_clear {
        font-size: 16px;
        line-height: 1;
        border: none;
        background: transparent;
        color: var(--header-text);
        cursor: pointer;
    }
    .provider_search_mode {
        padding: 3px 8px;
        border: 1px solid var(--header-separator);
        border-radius: 4px;
        background-color: transparent;
        color: var(--header-text);
        cursor: pointer;
        font-size: 13px;
    }
    .provider_search_mode.active {
        background-color: var(--header-text);
        color: var(--header);
    }
    .provider_search_empty {
        margin: 10px;
        color: var(--header-text);
    }
</style>
